import {
    Dialog,
    DialogContent,
    Grid2,
    Icon,
    IconButton,
    Typography,
    Box,
    Chip,
    Stack,
    CardContent,
    Card,
} from '@mui/material'
import React from 'react'
import { darkBlue } from 'utils/constants/ColorTheme'
import i18n from 'simple-react-i18n'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTable } from 'components/datatable/NewTable'
import useBoolean from 'utils/customHook/useBoolean'
import { downloadCSV } from 'utils/FileUtils'
import styled from '@emotion/styled'
import useSandreList from 'utils/customHook/useSandreList'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { EXPORT_IMPORT_THRESHOLD_HEADERS, THRESHOLD, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE, THRESHOLD_TYPE_CODE } from 'utils/QualityThresholdUtils'
import { isUndefined } from 'lodash'
import { getLabel } from 'utils/StoreUtils'
import { shallowEqual, useSelector } from 'react-redux'

const QUALITY_THRESHOLDS = [{
    thresholdCode: 1,
    thresholdType: THRESHOLD.PC,
    thresholds: [
        {
            parameterCode: '1340',
            unit: '162',
            fraction: '23',
            station: 1,
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            calciumClass: 1,
            frType: 1,
            fishCode: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
        {
            parameterCode: '1221',
            fraction: '23',
            threshold1: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
        {
            parameterCode: '1311',
            threshold1: 80,
            threshold2: 70,
            threshold3: 60,
            threshold4: 50,
            discriminant: THRESHOLD_DISCRIMINANT.INF_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.DECREASING,
        },
        {
            fraction: '23',
            threshold1: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}, {
    thresholdCode: 2,
    thresholdType: THRESHOLD.PC,
    thresholds: [
        {
            parameterCode: '1340',
            fraction: '23',
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}, {
    thresholdCode: 3,
    thresholdType: THRESHOLD.TAXON,
    thresholds: [
        {
            parameterCode: '887',
            station: 2,
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
        {
            parameterCode: '888',
            station: 2,
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
        {
            parameterCode: '889',
            station: 2,
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}, {
    thresholdCode: 4,
    thresholdType: THRESHOLD.TAXON,
    thresholds: [
        {
            parameterCode: '26',
            threshold1: 10,
            threshold2: 20,
            threshold3: 30,
            threshold4: 40,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}, {
    thresholdCode: 5,
    thresholdType: THRESHOLD.INDICE,
    thresholds: [
        {
            parameterCode: '5856',
            threshold1: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
        {
            parameterCode: '8973',
            threshold1: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}, {
    thresholdCode: 6,
    thresholdType: THRESHOLD.INDICE,
    thresholds: [
        {
            parameterCode: '5857',
            threshold1: 1,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
            display: THRESHOLD_DISPLAY.INCREASING,
            thresholdType: THRESHOLD_TYPE.INCREASING,
        },
    ],
}]

const THRESHOLDS = [
    { code: 1, name: 'Lim.', comment: 'Limite chimique', thresholdType: THRESHOLD.PC },
    { code: 2, name: 'Ref.', comment: 'Référence chimique', thresholdType: THRESHOLD.PC },
    { code: 3, name: 'Gam.', comment: 'Gammares', thresholdType: THRESHOLD.TAXON },
    { code: 4, name: 'Nem.', comment: 'Nemoura', thresholdType: THRESHOLD.TAXON },
    { code: 5, name: 'IBD', comment: 'Indice biologique diatomées', thresholdType: THRESHOLD.INDICE },
    { code: 6, name: 'IMOL.', comment: 'Indice mollusques', thresholdType: THRESHOLD.INDICE },
]

const ValueChip = styled(Chip)(() => ({
    fontSize: '0.75rem',
    height: '24px',
    fontFamily: 'monospace',
    '& .MuiChip-label': {
        padding: '0 8px',
    },
}))

const HelpImportQualityThreshold = () => {
    const {
        parameters,
        taxons,
        units,
        fractions,
        qualitometers,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        taxons: store.TaxonReducer.taxons,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const HEADERS = EXPORT_IMPORT_THRESHOLD_HEADERS.slice(0, -1)

    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)

    const THRESHOLD_TYPE_LABEL = {
        [THRESHOLD_TYPE.INCREASING]: i18n.increasing,
        [THRESHOLD_TYPE.DECREASING]: i18n.decreasing,
        [THRESHOLD_TYPE.BOUNDED]: i18n.bounded,
    }
    const THRESHOLD_DISPLAY_LABEL = {
        [THRESHOLD_DISPLAY.INCREASING]: i18n.increasing,
        [THRESHOLD_DISPLAY.DECREASING]: i18n.decreasing,
    }
    const DISCRIMINANT_SIGN = {
        [THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL]: '>=',
        [THRESHOLD_DISCRIMINANT.STRICTLY_SUP]: '>',
        [THRESHOLD_DISCRIMINANT.INF_OR_EQUAL]: '<=',
        [THRESHOLD_DISCRIMINANT.STRICTLY_INF]: '<',
        [THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL]: '<= =>',
        [THRESHOLD_DISCRIMINANT.STRICTLY_INF_OR_SUP]: '< >',
    }

    const EXEMPLE_ROWS = THRESHOLDS.flatMap(threshold => {
        const qualityThreshold = QUALITY_THRESHOLDS.find(qt => qt.thresholdCode === threshold.code)
        if (isUndefined(qualityThreshold)) {
            return [{
                code: threshold.code,
                name: threshold.name,
                comment: threshold.comment,
                parent: getLabel(THRESHOLDS, threshold.referenceCode),
            }]
        }
        const listReferencial = [THRESHOLD.PC, THRESHOLD.INDICE].includes(threshold.thresholdType) ? parameters : taxons
        return qualityThreshold.thresholds.map(t => {
            const qualitometer = qualitometers.find(q => q.id === t.station)
            return {
                thresholdCode: threshold.name,
                thresholdName: threshold.comment,
                type: THRESHOLD_TYPE_CODE[threshold.thresholdType],
                codeParameterOrTaxon: t.parameterCode,
                parameterOrTaxon: listReferencial.find(r => r.code === t.parameterCode)?.name,
                codeUnit: t.unit,
                unit: units.find(u => u.code === t.unit)?.symbolWithName,
                stationCode: qualitometer?.code,
                station: qualitometer?.name,
                fractionCode: t.fraction,
                fraction: fractions.find(f => f.id === t.fraction)?.name,
                thresholdType: THRESHOLD_TYPE_LABEL[t.thresholdType],
                displayMode: THRESHOLD_DISPLAY_LABEL[t.display],
                discriminant: DISCRIMINANT_SIGN[t.discriminant],
                threshold1: t.threshold1,
                threshold2: t.threshold2,
                threshold3: t.threshold3,
                threshold4: t.threshold4,
                hardnessCode: t.calciumClass,
                hardness: lexiconCalcium.find(l => l.code === t.calciumClass)?.name,
                frTypeCode: t.frType,
                frType: lexiconFRType.find(l => l.code === t.frType)?.name,
                fishContextCode: t.fishCode,
                fishContext: lexiconFishContext.find(l => l.code === t.fishCode)?.name,
            }
        })
    })

    return (
        <>
            <IconButton
                color='primary'
                onClick={onOpen}
                sx={{ color: darkBlue, fontSize: '2rem' }}
            >
                <Icon fontSize='inherit'>help_outline</Icon>
            </IconButton>
            <Dialog
                maxWidth='xl'
                open={isOpen}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: '90vw',
                            maxWidth: '90vw',
                            minHeight: '80vh',
                        },
                    },
                }}
            >
                <DefaultDialogTitle
                    title={i18n.csvImportGuideQualityThreshold}
                    onClose={onClose}
                />
                <DialogContent>
                    <Stack spacing={1}>
                        <Card>
                            <CardContent>
                                <Typography variant='h6' gutterBottom>
                                    {i18n.autorizeValue}
                                </Typography>
                                <Typography variant='body2' color='text.secondary' mb={3}>
                                    {i18n.useOnlyValueBelow}
                                </Typography>

                                <Grid2 container spacing={3}>
                                    {/* Types */}
                                    <Grid2 size={4}>
                                        <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                            {i18n.type}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='PC' size='small' />
                                                <Typography variant='caption' display='inline'>
                                                    {i18n.forThe} {i18n.PCparameters}
                                                </Typography>
                                            </Box>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='INDICE' size='small' />
                                                <Typography variant='caption' display='inline'>
                                                    {i18n.forThe} {i18n.indices}
                                                </Typography>
                                            </Box>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='TAXON' size='small' />
                                                <Typography variant='caption' display='inline'>
                                                    {i18n.forThe} {i18n.taxons}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid2>

                                    {/* Type de seuil */}
                                    <Grid2 size={4}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                                    {i18n.thresholdType}
                                                </Typography>
                                                <Stack direction='row' spacing={1}>
                                                    <ValueChip label={i18n.increasing} size='small' />
                                                    <ValueChip label={i18n.decreasing} size='small' />
                                                    <ValueChip label={i18n.bounded} size='small' />
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                                    {i18n.displayMode}
                                                </Typography>
                                                <Stack direction='row' spacing={1}>
                                                    <ValueChip label={i18n.increasing} size='small' />
                                                    <ValueChip label={i18n.decreasing} size='small' />
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Grid2>

                                    {/* Discriminant */}
                                    <Grid2 size={4}>
                                        <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                            {i18n.discriminant}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='>=' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.or}
                                                </Typography>
                                                <ValueChip label='>' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.forThe} {i18n.increasingThreshold}
                                                </Typography>
                                            </Box>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='<=' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.or}
                                                </Typography>
                                                <ValueChip label='<' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.forThe} {i18n.decreasingThreshold}
                                                </Typography>
                                            </Box>
                                            <Box display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                <ValueChip label='<= >=' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.or}
                                                </Typography>
                                                <ValueChip label='< >' size='small' />
                                                <Typography variant='caption'>
                                                    {i18n.forThe} {i18n.boundedThreshold}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid2>

                                    {/* Dureté */}
                                    <Grid2 size={4}>
                                        <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                            {i18n.hardness}
                                        </Typography>
                                        <Stack spacing={1}>
                                            {lexiconCalcium.map(lex => (
                                                <Box key={lex.code} display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                    <ValueChip
                                                        label={`${lex.code}`}
                                                        size='small'
                                                    />
                                                    <Typography variant='caption'>
                                                        {lex.name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Grid2>

                                    {/* Type FR */}
                                    <Grid2 size={4}>
                                        <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                            {i18n.frType}
                                        </Typography>
                                        <Stack direction='row' spacing={1}>
                                            {lexiconFRType.map(lex => (
                                                <ValueChip
                                                    key={lex.code}
                                                    label={lex.name}
                                                    size='small'
                                                />
                                            ))}
                                        </Stack>
                                    </Grid2>

                                    {/* Contexte piscicole */}
                                    <Grid2 size={4}>
                                        <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
                                            {i18n.fishContext}
                                        </Typography>
                                        <Stack spacing={1}>
                                            {lexiconFishContext.map(lex => (
                                                <Box key={lex.code} display='flex' alignItems='center' sx={{ gap: 0.5 }}>
                                                    <ValueChip
                                                        label={`${lex.code}`}
                                                        size='small'
                                                    />
                                                    <Typography variant='caption'>
                                                        {lex.name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Grid2>
                                </Grid2>
                            </CardContent>
                        </Card>
                        <CardTable
                            title={i18n.sampleDataOverview}
                            hideNbElements
                            actions={[
                                {
                                    icon: 'download',
                                    onClick: () => downloadCSV(HEADERS, EXEMPLE_ROWS, i18n.example),
                                },
                            ]}
                            headers={HEADERS}
                            rows={EXEMPLE_ROWS}
                            rowsPerPageOptions={nbPerPageLabel}
                        />
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default HelpImportQualityThreshold